// Calendar utility functions for exports, countdowns, stats, and GEDCOM parsing

/**
 * Parse GEDCOM date string to Date object
 */
export const parseGedcomDate = (dateStr) => {
  if (!dateStr) return null;
  
  const patterns = [
    /(\d{1,2})\s+(\w+)\s+(\d{4})/,  // "15 MAR 1920"
    /(\w+)\s+(\d{1,2}),?\s+(\d{4})/, // "March 15, 1920"
    /(\d{4})-(\d{1,2})-(\d{1,2})/,   // "1920-03-15"
    /(\d{1,2})\/(\d{1,2})\/(\d{4})/,  // "03/15/1920"
    /(\d{4})/                         // "1920" (year only)
  ];

  const monthNames = {
    'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
    'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11,
    'JANUARY': 0, 'FEBRUARY': 1, 'MARCH': 2, 'APRIL': 3, 'JUNE': 5,
    'JULY': 6, 'AUGUST': 7, 'SEPTEMBER': 8, 'OCTOBER': 9, 'NOVEMBER': 10, 'DECEMBER': 11
  };

  for (let i = 0; i < patterns.length; i++) {
    const pattern = patterns[i];
    const match = dateStr.toUpperCase().match(pattern);
    if (match) {
      if (pattern === patterns[0]) { // "15 MAR 1920"
        const day = parseInt(match[1]);
        const month = monthNames[match[2]];
        const year = parseInt(match[3]);
        if (month !== undefined) {
          return new Date(year, month, day);
        }
      } else if (pattern === patterns[1]) { // "March 15, 1920"
        const month = monthNames[match[1].toUpperCase()];
        const day = parseInt(match[2]);
        const year = parseInt(match[3]);
        if (month !== undefined) {
          return new Date(year, month, day);
        }
      } else if (pattern === patterns[2]) { // "1920-03-15"
        return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
      } else if (pattern === patterns[3]) { // "03/15/1920"
        return new Date(parseInt(match[3]), parseInt(match[1]) - 1, parseInt(match[2]));
      } else if (pattern === patterns[4]) { // "1920"
        return new Date(parseInt(match[1]), 0, 1);
      }
    }
  }
  return null;
};

/**
 * Extract all events from GEDCOM data
 */
export const extractEventsFromGedcom = (gedcomData) => {
  if (!gedcomData) return [];
  
  const { individuals, families } = gedcomData;
  const events = [];

  // Extract birth and death dates from individuals
  if (individuals) {
    Object.entries(individuals).forEach(([id, person]) => {
      if (person.birth) {
        const birthDate = parseGedcomDate(person.birth);
        if (birthDate) {
          events.push({
            id: `birth-${id}`,
            type: 'birth',
            date: birthDate,
            person: person.name,
            description: `${person.name} was born`,
            location: person.birthPlace || '',
            icon: '🎂'
          });
        }
      }

      if (person.death) {
        const deathDate = parseGedcomDate(person.death);
        if (deathDate) {
          events.push({
            id: `death-${id}`,
            type: 'death',
            date: deathDate,
            person: person.name,
            description: `${person.name} passed away`,
            location: person.deathPlace || '',
            icon: '🕊️'
          });
        }
      }
    });
  }

  // Extract marriage dates from families
  if (families) {
    Object.entries(families).forEach(([id, family]) => {
      if (family.marriage) {
        const marriageDate = parseGedcomDate(family.marriage);
        if (marriageDate) {
          const husband = individuals?.[family.husband]?.name || 'Unknown';
          const wife = individuals?.[family.wife]?.name || 'Unknown';
          events.push({
            id: `marriage-${id}`,
            type: 'marriage',
            date: marriageDate,
            person: `${husband} & ${wife}`,
            description: `${husband} married ${wife}`,
            location: family.marriagePlace || '',
            icon: '💒'
          });
        }
      }
    });
  }

  return events.sort((a, b) => a.date - b.date);
};

/**
 * Calculate anniversaries for the current year
 */
export const calculateAnniversaries = (events) => {
  const currentYear = new Date().getFullYear();
  const anniversaries = [];

  events.forEach(event => {
    const originalYear = event.date.getFullYear();
    const yearsAgo = currentYear - originalYear;
    
    if (yearsAgo > 0) {
      const anniversaryDate = new Date(currentYear, event.date.getMonth(), event.date.getDate());
      
      let anniversaryDescription = '';
      let anniversaryIcon = '';
      
      if (event.type === 'birth') {
        anniversaryDescription = `${event.person}'s ${yearsAgo}${getOrdinalSuffix(yearsAgo)} birthday`;
        anniversaryIcon = '🎉';
      } else if (event.type === 'death') {
        anniversaryDescription = `${yearsAgo} years since ${event.person} passed away`;
        anniversaryIcon = '🌹';
      } else if (event.type === 'marriage') {
        anniversaryDescription = `${event.person}'s ${yearsAgo}${getOrdinalSuffix(yearsAgo)} wedding anniversary`;
        anniversaryIcon = '💕';
      }
      
      anniversaries.push({
        id: `anniversary-${event.id}`,
        type: `${event.type}-anniversary`,
        date: anniversaryDate,
        person: event.person,
        description: anniversaryDescription,
        location: event.location,
        icon: anniversaryIcon,
        yearsAgo: yearsAgo,
        originalDate: event.date
      });
    }
  });

  return anniversaries;
};

/**
 * Get all events including anniversaries
 */
export const getAllEvents = (gedcomData) => {
  const originalEvents = extractEventsFromGedcom(gedcomData);
  const anniversaries = calculateAnniversaries(originalEvents);
  return [...originalEvents, ...anniversaries].sort((a, b) => a.date - b.date);
};

/**
 * Generate iCal (.ics) file content for calendar events
 */
export const generateICalContent = (events, title = 'Family Calendar') => {
  // Format date for iCal (not currently used but kept for future use)
  // const formatDate = (date) => {
  //   return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  // };

  const formatDateOnly = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const escapeText = (text) => {
    return text.replace(/[,;\\]/g, '\\$&').replace(/\n/g, '\\n');
  };

  let icalContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//MarcoDeMarco.net//Family Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeText(title)}`,
    'X-WR-TIMEZONE:America/New_York'
  ];

  events.forEach((event, index) => {
    const uid = `${event.id || index}@marcodemarco.net`;
    const dtstart = formatDateOnly(event.date);
    const summary = escapeText(event.description || event.person);
    const description = escapeText(
      `${event.description}${event.location ? `\\nLocation: ${event.location}` : ''}${
        event.yearsAgo ? `\\n${event.yearsAgo} years ago` : ''
      }`
    );

    icalContent.push(
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTART;VALUE=DATE:${dtstart}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      'STATUS:CONFIRMED',
      'TRANSP:TRANSPARENT',
      event.type.includes('anniversary') ? 'RRULE:FREQ=YEARLY' : '',
      'END:VEVENT'
    );
  });

  icalContent.push('END:VCALENDAR');
  return icalContent.filter(line => line !== '').join('\r\n');
};

/**
 * Download iCal file
 */
export const downloadICalFile = (events, filename = 'family-calendar.ics', title) => {
  const icalContent = generateICalContent(events, title);
  const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Calculate days until an event
 */
export const getDaysUntil = (eventDate) => {
  const today = new Date();
  const event = new Date(eventDate);
  
  // Set both dates to start of day for accurate comparison
  today.setHours(0, 0, 0, 0);
  event.setHours(0, 0, 0, 0);
  
  const diffTime = event - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Format countdown text
 */
export const formatCountdown = (days) => {
  if (days === 0) return 'Today!';
  if (days === 1) return 'Tomorrow';
  if (days < 0) return `${Math.abs(days)} days ago`;
  if (days <= 7) return `In ${days} days`;
  if (days <= 30) return `In ${days} days`;
  if (days <= 365) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? 'In 1 week' : `In ${weeks} weeks`;
  }
  const months = Math.floor(days / 30);
  return months === 1 ? 'In 1 month' : `In ${months} months`;
};

/**
 * Get upcoming events within specified days
 */
export const getUpcomingEvents = (events, days = 30) => {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + days);
  
  return events.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate >= today && eventDate <= futureDate;
  }).sort((a, b) => new Date(a.date) - new Date(b.date));
};

/**
 * Get events happening today
 */
export const getTodayEvents = (events) => {
  const today = new Date();
  return events.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate.getDate() === today.getDate() &&
           eventDate.getMonth() === today.getMonth() &&
           eventDate.getFullYear() === today.getFullYear();
  });
};

/**
 * Get events happening this month
 */
export const getThisMonthEvents = (events) => {
  const today = new Date();
  return events.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate.getMonth() === today.getMonth() &&
           eventDate.getFullYear() === today.getFullYear();
  }).sort((a, b) => new Date(a.date) - new Date(b.date));
};

/**
 * Get milestone events (special anniversaries)
 */
export const getMilestoneEvents = (events) => {
  const milestones = [25, 50, 75, 100, 125, 150];
  
  return events.filter(event => {
    if (!event.yearsAgo) return false;
    return milestones.includes(event.yearsAgo);
  });
};

/**
 * Get "On This Day" historical events
 */
export const getOnThisDayEvents = (events) => {
  const today = new Date();
  
  return events.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate.getDate() === today.getDate() &&
           eventDate.getMonth() === today.getMonth() &&
           eventDate.getFullYear() !== today.getFullYear() &&
           !event.type.includes('anniversary'); // Only original events, not anniversaries
  }).sort((a, b) => new Date(a.date) - new Date(b.date));
};

/**
 * Generate calendar statistics
 */
export const generateCalendarStats = (events, gedcomData) => {
  const today = new Date();
  const thisYear = today.getFullYear();
  
  const upcomingEvents = getUpcomingEvents(events, 30);
  const thisMonthEvents = getThisMonthEvents(events);
  const milestoneEvents = getMilestoneEvents(events);
  const onThisDayEvents = getOnThisDayEvents(events);
  
  // Calculate age ranges
  const birthEvents = events.filter(e => e.type === 'birth' && !e.type.includes('anniversary'));
  const ages = birthEvents.map(e => thisYear - new Date(e.date).getFullYear()).filter(age => age > 0);
  
  const stats = {
    totalEvents: events.length,
    upcomingCount: upcomingEvents.length,
    thisMonthCount: thisMonthEvents.length,
    milestonesCount: milestoneEvents.length,
    onThisDayCount: onThisDayEvents.length,
    oldestAge: ages.length > 0 ? Math.max(...ages) : 0,
    averageAge: ages.length > 0 ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0,
    totalFamilyMembers: gedcomData ? Object.keys(gedcomData.individuals || {}).length : 0,
    totalFamilies: gedcomData ? Object.keys(gedcomData.families || {}).length : 0,
    yearsOfHistory: gedcomData ? thisYear - 1866 : 0
  };
  
  return stats;
};

/**
 * Format date for display
 */
export const formatDisplayDate = (date, options = {}) => {
  const defaultOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  
  return new Date(date).toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Get ordinal suffix for numbers (1st, 2nd, 3rd, etc.)
 */
export const getOrdinalSuffix = (num) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};